import { Flex, FlexProps } from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  WindowScroller,
} from "react-virtualized";
import "react-virtualized/styles.css";

interface Props<T = any> extends FlexProps {
  rowHeight: number;
  items: T[];
  renderItem(item: T, index: number): any;
}

export default function VirtualScroll({ rowHeight, items, renderItem }: Props) {
  const scrollRef = useRef<any>();

  const cacheRef = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: rowHeight,
    })
  );

  const rowRenderer = useCallback(
    ({
      key, // Unique key within array of rows
      index, // Index of row within collection
      style, // Style object to be applied to row (to position it)
      parent,
    }: any) => {
      const item = items[index];

      return (
        <CellMeasurer
          key={key}
          parent={parent}
          cache={cacheRef.current}
          columnIndex={0}
          rowIndex={index}
        >
          <Flex height={`${rowHeight}px`} style={style}>
            {renderItem(item, index)}
          </Flex>
        </CellMeasurer>
      );
    },
    [items, renderItem, rowHeight]
  );

  return (
    <WindowScroller
      scrollElement={scrollRef?.current}
      onResize={() => {
        cacheRef.current.clearAll();
      }}
      onScroll={({ scrollTop }) => {
        scrollRef?.current?.scrollToPosition(scrollTop);
      }}
    >
      {({ height, isScrolling, registerChild, onChildScroll }) => {
        if (!height) {
          return <></>;
        }

        return (
          <AutoSizer>
            {({ width, height }) => {
              return (
                <div ref={registerChild as any}>
                  <List
                    width={width}
                    height={height}
                    rowCount={items.length}
                    rowHeight={rowHeight}
                    rowRenderer={rowRenderer}
                    isScrolling={isScrolling}
                    onChildScroll={onChildScroll}
                  />
                </div>
              );
            }}
          </AutoSizer>
        );
      }}
    </WindowScroller>
  );
}
